import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  Chip,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {
  rolePermissionService,
  Role,
  Permission,
} from '../../services/rolePermissionService';

const RolesManager: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [roleName, setRoleName] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rolesRes, permissionsRes] = await Promise.all([
        rolePermissionService.getRoles(),
        rolePermissionService.getPermissions(),
      ]);
      console.log(permissionsRes);
      setRoles(rolesRes.data.roles || []);
      setPermissions(permissionsRes.data.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setRoles([]);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (role?: Role) => {
    if (role) {
      setEditingRole(role);
      setRoleName(role.name);
      setSelectedPermissions(role.permissions.map(p => p.id));
    } else {
      setEditingRole(null);
      setRoleName('');
      setSelectedPermissions([]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRole(null);
    setRoleName('');
    setSelectedPermissions([]);
  };

  const handleSubmit = async () => {
    try {
      if (editingRole) {
        await rolePermissionService.updateRole(editingRole.id, {
          name: roleName,
          permissions: selectedPermissions,
        });
      } else {
        await rolePermissionService.createRole({
          name: roleName,
          permissions: selectedPermissions,
        });
      }
      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error('Error saving role:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this role?')) {
      try {
        await rolePermissionService.deleteRole(id);
        loadData();
      } catch (error) {
        console.error('Error deleting role:', error);
      }
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant='h5'>Roles Management</Typography>
        <Button
          variant='contained'
          color='primary'
          onClick={() => handleOpenDialog()}
        >
          Add New Role
        </Button>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
          },
          gap: 3,
        }}
      >
        {roles.map(role => (
          <Card key={role.id} sx={{ width: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant='h6'>{role.name}</Typography>
                <Box>
                  <IconButton
                    size='small'
                    onClick={() => handleOpenDialog(role)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size='small'
                    onClick={() => handleDelete(role.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {role.permissions.map(permission => (
                  <Chip
                    key={permission.id}
                    label={permission.name}
                    size='small'
                    variant='outlined'
                    onClick={() => false}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          {editingRole ? 'Edit Role' : 'Create New Role'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin='dense'
            label='Role Name'
            fullWidth
            value={roleName}
            onChange={e => setRoleName(e.target.value)}
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant='subtitle2' gutterBottom>
              Permissions
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {permissions.map(permission => (
                <Chip
                  key={permission.id}
                  label={permission.name}
                  onClick={() => {
                    setSelectedPermissions(prev =>
                      prev.includes(permission.id)
                        ? prev.filter(id => id !== permission.id)
                        : [...prev, permission.id]
                    );
                  }}
                  color={
                    selectedPermissions.includes(permission.id)
                      ? 'primary'
                      : 'default'
                  }
                  variant={
                    selectedPermissions.includes(permission.id)
                      ? 'filled'
                      : 'outlined'
                  }
                  
                />
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant='contained' color='primary'>
            {editingRole ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RolesManager;
